// frontend/src/components/FinancialSummary.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import api from '../utils/api';

const FinancialSummary = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 100 }
        });
        setDays(response.data.days || []);
        if (response.data.days && response.data.days.length > 0) {
          setSelectedDay(response.data.days[0]);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        setError('Error al cargar los días disponibles.');
      }
    };
    if (!loading && user && user.is_notario) {
      fetchDays();
    }
  }, [user, loading]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedDay) {
      toast.error('Por favor, selecciona un día.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsLoading(true);
    setError(null);
    setSummary(null);

    try {
      const response = await api.post('/api/audit/financial-summary/', {
        fecha_documento: selectedDay,
      });
      setSummary(response.data);
      toast.success('Resumen financiero generado.', { position: 'top-right', autoClose: 3000 });
    } catch (error) {
      setError(error.response?.data?.error || 'Error al generar el resumen financiero.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDay]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Resumen Financiero por Día</h1>
        </div>
        <Link
          to="/dashboard/notario"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
          aria-label="Volver al Dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-6 rounded-xl shadow-2xl"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-6">Seleccionar Día para el Resumen</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-full sm:w-1/3">
                <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                  Día de los Trámites
                </label>
                <select
                  id="fecha_documento"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                  className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  required
                >
                  <option value="">Seleccione un día</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              <motion.button
                type="submit"
                disabled={isLoading}
                className={`w-full sm:w-auto mt-4 sm:mt-0 bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-6 rounded-lg hover:bg-notaria-red hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 hover:shadow-xl ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
                aria-label="Generar resumen financiero"
              >
                {isLoading ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  'Generar Resumen'
                )}
              </motion.button>
            </div>
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-center"
              >
                {error}
              </motion.div>
            )}
          </form>

          {summary && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 bg-notaria-blue-2 p-4 rounded-lg"
            >
              <h3 className="text-lg font-semibold mb-3">Resumen Financiero para {selectedDay}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <p><span className="font-medium">Total de Trámites:</span> {summary.total_tramites}</p>
                <p><span className="font-medium">Valor Total Recaudado:</span> ${summary.total_recaudado.toFixed(2)}</p>
                <p><span className="font-medium">Falta por Justificar/Cobrar:</span> ${summary.total_falta_justificar.toFixed(2)}</p>
                <p><span className="font-medium">Total Item:</span> ${summary.total_comision_auto.toFixed(2)}</p>
                <p><span className="font-medium">Total Reconocimientos:</span> {summary.total_reconocimientos}</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default FinancialSummary;